/* eslint-disable */
import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Link,
} from '@mui/material';
import { Warning } from '@mui/icons-material';
import type { EpicDeliveryItem } from '../../types/productDashboard';

interface DeliveryTicketListProps {
  epics: EpicDeliveryItem[];
}

export const DeliveryTicketList: React.FC<DeliveryTicketListProps> = ({ epics }) => {
  const jiraBaseUrl = 'https://outsight.atlassian.net/browse';

  if (epics.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No delivery tickets found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {epics.map((epic, index) => {
        const isStandaloneStories = epic.key === '';
        const allTickets = [...epic.stories, ...epic.tasks];

        if (allTickets.length === 0) {
          return null;
        }

        return (
          <Box key={epic.key || `standalone-${index}`} sx={{ mb: 2.5 }}>
            {/* Epic Header */}
            <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              {isStandaloneStories ? (
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                  Standalone Stories
                </Typography>
              ) : (
                <>
                  <Link
                    href={`${jiraBaseUrl}/${epic.key}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                  >
                    <Chip
                      label={epic.key}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      clickable
                      sx={{
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'secondary.light',
                          color: 'secondary.contrastText',
                        },
                      }}
                    />
                  </Link>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                    {epic.title}
                  </Typography>
                  <Chip label={epic.status} size="small" variant="outlined" />
                </>
              )}
            </Box>

            {/* Stories/Tasks Table */}
            <Table
              size="small"
              sx={{
                backgroundColor: 'grey.50',
                borderRadius: 1,
                '& .MuiTableCell-root': {
                  px: 1.5,
                  py: 1.25,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Key</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>Title</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', width: '120px' }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', width: '140px' }}>
                    Fix Version
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', width: '60px' }}>
                    Late
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {allTickets.map((ticket) => (
                  <TableRow key={ticket.key} hover>
                    {/* Key (Jira link) */}
                    <TableCell>
                      <Link
                        href={`${jiraBaseUrl}/${ticket.key}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        sx={{ fontWeight: 'medium', fontSize: '0.8rem' }}
                      >
                        {ticket.key}
                      </Link>
                    </TableCell>

                    {/* Title */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {ticket.title}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell>
                      <Chip
                        label={ticket.status}
                        size="small"
                        color={
                          ticket.status === 'Done' || ticket.status === 'Integrated'
                            ? 'success'
                            : ticket.status === 'In Development' || ticket.status === 'In Progress'
                            ? 'primary'
                            : 'default'
                        }
                        sx={{ fontSize: '0.7rem' }}
                      />
                    </TableCell>

                    {/* Fix Version */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>
                        {ticket.fix_version || (
                          <span style={{ fontStyle: 'italic', color: '#999' }}>Unplanned</span>
                        )}
                      </Typography>
                    </TableCell>

                    {/* Late Indicator */}
                    <TableCell align="center">
                      {ticket.is_late && (
                        <Warning
                          fontSize="small"
                          color="error"
                          titleAccess="This ticket is scheduled after the expected version"
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        );
      })}
    </Box>
  );
};
